/* =============================================================
   DocumentTypesSection.jsx — Document types management in Settings
   -------------------------------------------------------------
   Allows the IO manager to:
     • See all existing document types
     • Create new ones (name + icon)
     • Upload a template file per document type
     • Delete unused document types

   Which students see a document is determined entirely by the
   process template assigned to them — documents in a process step
   are shown to all students in that process.
   ============================================================= */

import { useState } from 'react';
import React from 'react';
import { useDocumentTypes } from '../hooks/useDocumentTypes';

/* ─── ICON OPTIONS ───────────────────────────────────────────── */
const ICON_OPTIONS = [
  { value: 'draft',           label: 'Document'       },
  { value: 'description',     label: 'Description'    },
  { value: 'article',         label: 'Article'        },
  { value: 'assignment',      label: 'Assignment'     },
  { value: 'badge',           label: 'Badge / ID'     },
  { value: 'contact_page',    label: 'Contact Page'   },
  { value: 'health_and_safety', label: 'Health'       },
  { value: 'receipt_long',    label: 'Receipt'        },
  { value: 'school',          label: 'School'         },
  { value: 'translate',       label: 'Translation'    },
  { value: 'fact_check',      label: 'Fact Check'     },
  { value: 'fingerprint',     label: 'Identity'       },
  { value: 'travel_explore',  label: 'Travel'         },
  { value: 'home_work',       label: 'Housing'        },
  { value: 'payments',        label: 'Payments'       },
];

/* ─── DOCUMENT TYPE ROW ──────────────────────────────────────── */
function DocTypeRow({ doc, onDelete, onUploadTemplate, onRemoveTemplate, saving }) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [templateUploading, setTemplateUploading] = useState(false);
  const [templateRemoving, setTemplateRemoving] = useState(false);
  const templateInputRef = React.useRef(null);

  const handleDelete = async () => {
    setDeleting(true);
    try { await onDelete(doc.id); }
    catch { setDeleting(false); }
  };

  const handleTemplateUpload = async (file) => {
    if (!file) return;
    setTemplateUploading(true);
    try { await onUploadTemplate(doc.id, file); }
    catch {}
    setTemplateUploading(false);
  };

  const handleTemplateRemove = async () => {
    setTemplateRemoving(true);
    try { await onRemoveTemplate(doc.id); }
    catch {}
    setTemplateRemoving(false);
  };

  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      {/* Row header */}
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <span className="material-symbols-outlined text-[16px] text-slate-500">{doc.icon ?? 'draft'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#0b1c30]">{doc.name}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {doc.template_file_name
              ? <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[11px]">attach_file</span>{doc.template_file_name}</span>
              : 'No template'}
          </p>
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors px-2 py-1 rounded-lg hover:bg-indigo-50"
        >
          <span className="material-symbols-outlined text-[14px]">tune</span>
          Configure
          <span className="material-symbols-outlined text-[14px]">
            {expanded ? 'expand_less' : 'expand_more'}
          </span>
        </button>
        <button
          onClick={handleDelete}
          disabled={deleting || saving}
          className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
          title="Delete document type"
        >
          <span className="material-symbols-outlined text-[16px]">
            {deleting ? 'hourglass_empty' : 'delete'}
          </span>
        </button>
      </div>

      {/* Expanded configuration — template only */}
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-4 space-y-3">

          {/* Template upload */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Template file
            </p>
            <p className="text-xs text-slate-400 mb-3 leading-relaxed">
              Upload a pre-filled form that students will download, fill out, and re-upload.
            </p>

            {doc.template_file_name ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                  <span className="material-symbols-outlined text-[15px] text-indigo-500 flex-shrink-0">attach_file</span>
                  <span className="text-xs font-medium text-indigo-700 truncate">{doc.template_file_name}</span>
                </div>
                <button
                  onClick={() => templateInputRef.current?.click()}
                  disabled={templateUploading || templateRemoving || saving}
                  className="flex items-center gap-1 px-2.5 py-2 border border-slate-200 text-slate-500 text-xs font-medium rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 flex-shrink-0"
                  title="Replace template"
                >
                  <span className="material-symbols-outlined text-[14px]">swap_horiz</span>
                  Replace
                </button>
                <button
                  onClick={handleTemplateRemove}
                  disabled={templateUploading || templateRemoving || saving}
                  className="flex items-center gap-1 px-2.5 py-2 border border-red-200 text-red-500 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  {templateRemoving
                    ? <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
                    : <span className="material-symbols-outlined text-[14px]">delete</span>}
                </button>
              </div>
            ) : (
              <button
                onClick={() => templateInputRef.current?.click()}
                disabled={templateUploading || saving}
                className="flex items-center gap-1.5 px-3 py-2 border border-dashed border-slate-300 text-slate-500 text-xs font-medium rounded-lg hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-50"
              >
                {templateUploading
                  ? <><span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>Uploading…</>
                  : <><span className="material-symbols-outlined text-[14px]">upload_file</span>Upload template</>}
              </button>
            )}

            <input
              ref={templateInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              className="hidden"
              onChange={e => { handleTemplateUpload(e.target.files[0]); e.target.value = ''; }}
            />
          </div>
        </div>
      )}
    </div>
  );
}


/* ─── TASK TYPE ROW ──────────────────────────────────────────── */
function TaskTypeRow({ task, onDelete, saving }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try { await onDelete(task.id); }
    catch { setDeleting(false); }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 border border-slate-100 rounded-xl hover:bg-slate-50 transition-colors">
      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
        <span className="material-symbols-outlined text-[16px] text-emerald-500">check_box</span>
      </div>
      <span className="flex-1 text-sm font-medium text-[#0b1c30]">{task.name}</span>
      <button
        onClick={handleDelete}
        disabled={deleting || saving}
        className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
        title="Delete task type"
      >
        <span className="material-symbols-outlined text-[16px]">
          {deleting ? 'hourglass_empty' : 'delete'}
        </span>
      </button>
    </div>
  );
}


/* ─── DOCUMENT TYPES SECTION ─────────────────────────────────── */
export default function DocumentTypesSection() {
  const {
    docTypes, taskTypes, loading, saving, error,
    createDocType, createTaskType, deleteDocType, uploadTemplate, removeTemplate,
  } = useDocumentTypes();

  const [activeTab,  setActiveTab]  = useState('documents');

  // Document form state
  const [newDocName,  setNewDocName]  = useState('');
  const [newDocIcon,  setNewDocIcon]  = useState('draft');
  const [creatingDoc, setCreatingDoc] = useState(false);
  const [docNameError, setDocNameError] = useState('');

  // Task form state
  const [newTaskName,  setNewTaskName]  = useState('');
  const [creatingTask, setCreatingTask] = useState(false);
  const [taskNameError, setTaskNameError] = useState('');

  const handleCreateDoc = async () => {
    if (!newDocName.trim()) { setDocNameError('Name is required'); return; }
    setCreatingDoc(true);
    try {
      await createDocType({ name: newDocName, icon: newDocIcon, requiredFor: [] });
      setNewDocName('');
      setNewDocIcon('draft');
      setDocNameError('');
    } catch {}
    setCreatingDoc(false);
  };

  const handleCreateTask = async () => {
    if (!newTaskName.trim()) { setTaskNameError('Name is required'); return; }
    setCreatingTask(true);
    try {
      await createTaskType(newTaskName);
      setNewTaskName('');
      setTaskNameError('');
    } catch {}
    setCreatingTask(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      {/* Header + tab switcher */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px] text-indigo-600">folder_managed</span>
          <h2 className="font-display text-base font-semibold text-[#0b1c30]">Document Types</h2>
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          {[
            { value: 'documents', label: 'Documents', icon: 'draft'     },
            { value: 'tasks',     label: 'Tasks',     icon: 'check_box' },
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium transition-colors ${
                activeTab === tab.value ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <span className="material-symbols-outlined text-[13px]">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-6 mt-4 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
          <span className="material-symbols-outlined text-[14px]">error</span>
          {error}
        </div>
      )}

      {/* ── DOCUMENTS TAB ─────────────────────────────────────── */}
      {activeTab === 'documents' && (
        <div className="p-6 space-y-4">
          <p className="text-xs text-slate-400 leading-relaxed">
            Create the documents that can be linked to process steps. Which students see a document
            is determined by the process template assigned to them. You can optionally upload a
            template file that students will download, fill out, and re-upload.
          </p>

          {loading ? (
            <div className="space-y-2 animate-pulse">
              {[1,2,3].map(i => <div key={i} className="h-14 bg-slate-100 rounded-xl" />)}
            </div>
          ) : docTypes.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">
              No document types yet. Create your first one below.
            </p>
          ) : (
            <div className="space-y-2">
              {docTypes.map(doc => (
                <DocTypeRow
                  key={doc.id}
                  doc={doc}
                  onDelete={deleteDocType}
                  onUploadTemplate={uploadTemplate}
                  onRemoveTemplate={removeTemplate}
                  saving={saving}
                />
              ))}
            </div>
          )}

          {/* Create new document type */}
          <div className="border-t border-slate-100 pt-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
              Add Document Type
            </p>
            <div className="flex gap-2 items-start">
              <div className="flex items-center gap-1.5 border border-slate-200 rounded-xl bg-white px-2 py-2 flex-shrink-0">
                <div className="w-6 h-6 flex items-center justify-center">
                  <span className="material-symbols-outlined text-[18px] text-slate-500">{newDocIcon}</span>
                </div>
                <select
                  value={newDocIcon}
                  onChange={e => setNewDocIcon(e.target.value)}
                  className="text-xs text-slate-600 bg-transparent focus:outline-none cursor-pointer pr-1"
                >
                  {ICON_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={newDocName}
                  onChange={e => { setNewDocName(e.target.value); setDocNameError(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateDoc(); }}
                  placeholder="e.g. Visa Application"
                  className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition
                    ${docNameError ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                />
                {docNameError && <p className="text-xs text-red-500 mt-1">{docNameError}</p>}
              </div>
              <button
                onClick={handleCreateDoc}
                disabled={creatingDoc || saving}
                className="px-4 py-2.5 bg-[#4343d5] text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-60 flex items-center gap-1.5 flex-shrink-0"
              >
                <span className="material-symbols-outlined text-[16px]">
                  {creatingDoc ? 'hourglass_empty' : 'add'}
                </span>
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TASKS TAB ─────────────────────────────────────────── */}
      {activeTab === 'tasks' && (
        <div className="p-6 space-y-4">
          <p className="text-xs text-slate-400 leading-relaxed">
            Tasks are checklist items that students confirm they've completed — no document upload required.
            Examples: <em>"Sign orientation form"</em>, <em>"Attend welcome meeting"</em>, <em>"Activate university account"</em>.
            Once created here, tasks can be added to any process step in Exchange Process.
          </p>

          {loading ? (
            <div className="space-y-2 animate-pulse">
              {[1,2].map(i => <div key={i} className="h-12 bg-slate-100 rounded-xl" />)}
            </div>
          ) : taskTypes.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">
              No task types yet. Create your first one below.
            </p>
          ) : (
            <div className="space-y-2">
              {taskTypes.map(task => (
                <TaskTypeRow
                  key={task.id}
                  task={task}
                  onDelete={deleteDocType}
                  saving={saving}
                />
              ))}
            </div>
          )}

          {/* Create new task type */}
          <div className="border-t border-slate-100 pt-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
              Add Task Type
            </p>
            <div className="flex gap-2 items-start">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0 border border-emerald-100">
                <span className="material-symbols-outlined text-[18px] text-emerald-500">check_box</span>
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={newTaskName}
                  onChange={e => { setNewTaskName(e.target.value); setTaskNameError(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateTask(); }}
                  placeholder="e.g. Sign orientation form"
                  className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-200 text-[#0b1c30] transition
                    ${taskNameError ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                />
                {taskNameError && <p className="text-xs text-red-500 mt-1">{taskNameError}</p>}
              </div>
              <button
                onClick={handleCreateTask}
                disabled={creatingTask || saving}
                className="px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-60 flex items-center gap-1.5 flex-shrink-0"
              >
                <span className="material-symbols-outlined text-[16px]">
                  {creatingTask ? 'hourglass_empty' : 'add'}
                </span>
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
